import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import {
  approveMonthlyReport,
  approvePost,
  deleteMonthlyReport,
  deletePost,
  getOperatorRole,
  getUserProfile,
  listPendingFarmerReports,
  listPendingPosts,
  rejectMonthlyReport,
  rejectPost,
} from '../lib/firestore';

export default function AdminReviewPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [pendingPosts, setPendingPosts] = useState([]);
  const [pendingReports, setPendingReports] = useState([]);
  const [selectedPostId, setSelectedPostId] = useState('');
  const [selectedReportKey, setSelectedReportKey] = useState('');
  const [activeModal, setActiveModal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [postActionStatus, setPostActionStatus] = useState(null);
  const [reportActionStatus, setReportActionStatus] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null);
  const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID || '';
  const rolesAdminConsoleUrl = user?.uid && projectId
    ? `https://console.firebase.google.com/project/${projectId}/firestore/data/~2FrolesAdmins~2F${user.uid}`
    : '';
  const firestoreRulesConsoleUrl = projectId
    ? `https://console.firebase.google.com/project/${projectId}/firestore/rules`
    : '';
  const showDebug = (() => {
    if (searchParams.get('debug') === '1') return true;
    if (searchParams.get('debug') === 'true') return true;
    if (typeof window === 'undefined') return false;
    return /[?&]debug=1(?:&|$)/.test(window.location.href);
  })();

  async function collectDebugInfo() {
    if (!user?.uid) return null;
    const [operatorRole, userProfile] = await Promise.all([
      getOperatorRole(user.uid),
      getUserProfile(user.uid),
    ]);

    return {
      projectId,
      uid: user.uid,
      email: user.email || '',
      operatorRole: operatorRole || '(none)',
      profileRole: userProfile?.role || '(none)',
      displayName: userProfile?.name || '',
      rolesAdminPath: `rolesAdmins/${user.uid}`,
      userPath: `users/${user.uid}`,
    };
  }

  async function handleDumpDebugInfo() {
    const nextInfo = await collectDebugInfo();
    setDebugInfo(nextInfo);
    console.group('AdminReviewPage debug');
    console.log(nextInfo);
    console.log('Expected Firestore role source:', 'rolesAdmins/{uid}.role');
    console.log('If operatorRole is (none), account recreation is unnecessary. Add/update the rolesAdmins document instead.');
    console.groupEnd();
  }

  async function loadPending() {
    setLoading(true);
    setError('');
    try {
      const [postsResult, reportsResult] = await Promise.allSettled([
        listPendingPosts(),
        listPendingFarmerReports(),
      ]);

      const nextDebugInfo = await collectDebugInfo();
      const issues = [];

      if (postsResult.status === 'fulfilled') {
        setPendingPosts(postsResult.value);
        if (!selectedPostId && postsResult.value[0]?.id) {
          setSelectedPostId(postsResult.value[0].id);
        }
        if (selectedPostId && !postsResult.value.some((row) => row.id === selectedPostId)) {
          setSelectedPostId(postsResult.value[0]?.id || '');
        }
      } else {
        setPendingPosts([]);
        issues.push(`運営記事: ${postsResult.reason?.code || postsResult.reason?.message || 'read-failed'}`);
      }

      if (reportsResult.status === 'fulfilled') {
        setPendingReports(reportsResult.value);
        const firstReportKey = reportsResult.value[0] ? `${reportsResult.value[0].farmerId}:${reportsResult.value[0].id}` : '';
        if (!selectedReportKey && firstReportKey) {
          setSelectedReportKey(firstReportKey);
        }
        if (selectedReportKey && !reportsResult.value.some((row) => `${row.farmerId}:${row.id}` === selectedReportKey)) {
          setSelectedReportKey(firstReportKey);
        }
      } else {
        setPendingReports([]);
        issues.push(`農家近況報告: ${reportsResult.reason?.code || reportsResult.reason?.message || 'read-failed'}`);
      }

      if (issues.length) {
        setError(`承認待ちデータの一部または全部を読めません。 ${issues.join(' / ')}`);
      }

      setDebugInfo({
        ...(nextDebugInfo || {}),
        pendingPostsRead: postsResult.status,
        pendingReportsRead: reportsResult.status,
        pendingPostsError: postsResult.status === 'rejected' ? (postsResult.reason?.code || postsResult.reason?.message || '') : '',
        pendingReportsError: reportsResult.status === 'rejected' ? (reportsResult.reason?.code || reportsResult.reason?.message || '') : '',
      });
    } catch (err) {
      setError(`承認待ちデータの読み込みに失敗しました。 ${err?.code || err?.message || ''}`.trim());
      setPendingPosts([]);
      setPendingReports([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadPending();
  }, []);

  useEffect(() => {
    if (!showDebug) {
      setDebugInfo(null);
      return;
    }
    collectDebugInfo().then(setDebugInfo).catch(() => {});
  }, [showDebug, user?.uid]);

  useEffect(() => {
    function handleKeydown(event) {
      if (event.key === 'Escape') {
        setActiveModal(null);
      }
    }

    if (!activeModal) return undefined;
    window.addEventListener('keydown', handleKeydown);
    return () => window.removeEventListener('keydown', handleKeydown);
  }, [activeModal]);

  useEffect(() => {
    if (!activeModal) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [activeModal]);

  async function handleApprovePost(postId) {
    try {
      await approvePost(postId, user.uid);
      setPendingPosts((prev) => prev.filter((post) => post.id !== postId));
      setSelectedPostId((prev) => (prev === postId ? '' : prev));
      setStatus('運営記事を承認しました。');
      setPostActionStatus({ type: 'success', text: '承認しました。' });
      setActiveModal(null);
      await loadPending();
    } catch (err) {
      setPostActionStatus({ type: 'error', text: `承認に失敗しました。 ${err?.code || err?.message || ''}`.trim() });
    }
  }

  async function handleRejectPost(postId) {
    const note = window.prompt('差し戻し理由を入力してください（任意）', '') || '';
    try {
      await rejectPost(postId, user.uid, note);
      setPendingPosts((prev) => prev.filter((post) => post.id !== postId));
      setSelectedPostId((prev) => (prev === postId ? '' : prev));
      setStatus('運営記事を差し戻しました。');
      setPostActionStatus({ type: 'success', text: '差し戻しました。' });
      setActiveModal(null);
      await loadPending();
    } catch (err) {
      setPostActionStatus({ type: 'error', text: `差し戻しに失敗しました。 ${err?.code || err?.message || ''}`.trim() });
    }
  }

  async function handleApproveReport(farmerId, reportId) {
    try {
      await approveMonthlyReport(farmerId, reportId, user.uid);
      setPendingReports((prev) => prev.filter((report) => !(report.farmerId === farmerId && report.id === reportId)));
      setSelectedReportKey((prev) => (prev === `${farmerId}:${reportId}` ? '' : prev));
      setStatus('農家近況報告を承認しました。');
      setReportActionStatus({ type: 'success', text: '承認しました。' });
      setActiveModal(null);
      await loadPending();
    } catch (err) {
      setReportActionStatus({ type: 'error', text: `承認に失敗しました。 ${err?.code || err?.message || ''}`.trim() });
    }
  }

  async function handleRejectReport(farmerId, reportId) {
    const note = window.prompt('差し戻し理由を入力してください（任意）', '') || '';
    try {
      await rejectMonthlyReport(farmerId, reportId, user.uid, note);
      setPendingReports((prev) => prev.filter((report) => !(report.farmerId === farmerId && report.id === reportId)));
      setSelectedReportKey((prev) => (prev === `${farmerId}:${reportId}` ? '' : prev));
      setStatus('農家近況報告を差し戻しました。');
      setReportActionStatus({ type: 'success', text: '差し戻しました。' });
      setActiveModal(null);
      await loadPending();
    } catch (err) {
      setReportActionStatus({ type: 'error', text: `差し戻しに失敗しました。 ${err?.code || err?.message || ''}`.trim() });
    }
  }

  async function handleDeletePost(postId) {
    if (!window.confirm('この運営記事を削除しますか？')) return;
    try {
      await deletePost(postId);
      setPendingPosts((prev) => prev.filter((post) => post.id !== postId));
      setSelectedPostId((prev) => (prev === postId ? '' : prev));
      setStatus('運営記事を削除しました。');
      setPostActionStatus({ type: 'success', text: '削除しました。' });
      setActiveModal(null);
      await loadPending();
    } catch (err) {
      setPostActionStatus({ type: 'error', text: `削除に失敗しました。 ${err?.code || err?.message || ''}`.trim() });
    }
  }

  async function handleDeleteReport(farmerId, reportId) {
    if (!window.confirm('この農家近況報告を削除しますか？')) return;
    try {
      await deleteMonthlyReport(farmerId, reportId);
      setPendingReports((prev) => prev.filter((report) => !(report.farmerId === farmerId && report.id === reportId)));
      setSelectedReportKey((prev) => (prev === `${farmerId}:${reportId}` ? '' : prev));
      setStatus('農家近況報告を削除しました。');
      setReportActionStatus({ type: 'success', text: '削除しました。' });
      setActiveModal(null);
      await loadPending();
    } catch (err) {
      setReportActionStatus({ type: 'error', text: `削除に失敗しました。 ${err?.code || err?.message || ''}`.trim() });
    }
  }

  function formatDateLabel(value) {
    if (!value) return '日付未設定';
    return String(value);
  }

  function openPostModal(postId) {
    setSelectedPostId(postId);
    setActiveModal({ type: 'post', id: postId });
  }

  function openReportModal(reportKey) {
    setSelectedReportKey(reportKey);
    setActiveModal({ type: 'report', id: reportKey });
  }

  function canReview(item) {
    return item?.createdBy !== user?.uid;
  }

  const selectedPost = pendingPosts.find((post) => post.id === selectedPostId) || null;
  const selectedReport = pendingReports.find((report) => `${report.farmerId}:${report.id}` === selectedReportKey) || null;

  return (
    <>
      <Helmet><title>Drs.Farm | 記事承認</title></Helmet>
      <h1>記事承認</h1>
      <p className="hint">運営記事と農家近況報告の承認・差し戻しを行います。</p>
      {status ? <p className="success-text">{status}</p> : null}
      {error ? <p className="error-text">{error}</p> : null}

      <section className="panel">
        <div className="row-actions">
          <Link className="btn btn-secondary" to="/admin">管理者メニューへ戻る</Link>
          <button className="btn btn-outline-solid" type="button" onClick={loadPending} disabled={loading}>
            {loading ? '更新中...' : '再読み込み'}
          </button>
          {showDebug ? (
            <button className="btn btn-outline-solid" type="button" onClick={handleDumpDebugInfo}>
              デバッグ情報をコンソール出力
            </button>
          ) : null}
        </div>
        {showDebug && debugInfo ? (
          <div className="info-callout info-callout-compact">
            <p className="info-callout-title">承認画面デバッグ</p>
            <p>projectId: {debugInfo.projectId || '(none)'}</p>
            <p>uid: {debugInfo.uid}</p>
            <p>email: {debugInfo.email || '(none)'}</p>
            <p>rolesAdmins.role: {debugInfo.operatorRole}</p>
            <p>users.role: {debugInfo.profileRole}</p>
            <p>pendingPosts read: {debugInfo.pendingPostsRead || '(not-run)'}</p>
            <p>pendingReports read: {debugInfo.pendingReportsRead || '(not-run)'}</p>
            {debugInfo.pendingPostsError ? <p>pendingPosts error: {debugInfo.pendingPostsError}</p> : null}
            {debugInfo.pendingReportsError ? <p>pendingReports error: {debugInfo.pendingReportsError}</p> : null}
            <div className="row-actions">
              {rolesAdminConsoleUrl ? (
                <a className="btn btn-secondary" href={rolesAdminConsoleUrl} target="_blank" rel="noreferrer">
                  rolesAdmins を開く
                </a>
              ) : null}
              {firestoreRulesConsoleUrl ? (
                <a className="btn btn-secondary" href={firestoreRulesConsoleUrl} target="_blank" rel="noreferrer">
                  Firestore Rules を開く
                </a>
              ) : null}
            </div>
            <p className="hint">`rolesAdmins/{'{uid}'}.role` が `site_admin` または運営用ロールでないと、管理系権限として認識されません。</p>
          </div>
        ) : null}
      </section>

      <section className="admin-grid">
        <article className="panel">
          <h2>承認待ち: 運営記事</h2>
          {postActionStatus ? (
            <p className={postActionStatus.type === 'success' ? 'success-text' : 'error-text'}>{postActionStatus.text}</p>
          ) : null}
          {pendingPosts.length === 0 ? <p>承認待ちの記事はありません。</p> : (
            <>
              <div className="review-card-list" role="list">
                {pendingPosts.map((post) => (
                  <button
                    key={post.id}
                    type="button"
                    className={`review-card ${selectedPostId === post.id ? 'is-selected' : ''}`}
                    onClick={() => openPostModal(post.id)}
                  >
                    <span className="review-card-type">運営記事</span>
                    <strong>{post.title || '(無題)'}</strong>
                    <span className="review-card-date">{formatDateLabel(post.publishedDate)}</span>
                    {!canReview(post) ? (
                      <span className="review-card-note">投稿本人なので承認できません</span>
                    ) : null}
                  </button>
                ))}
              </div>
            </>
          )}
        </article>

        <article className="panel">
          <h2>承認待ち: 農家近況報告</h2>
          {reportActionStatus ? (
            <p className={reportActionStatus.type === 'success' ? 'success-text' : 'error-text'}>{reportActionStatus.text}</p>
          ) : null}
          {pendingReports.length === 0 ? <p>承認待ちの近況報告はありません。</p> : (
            <>
              <div className="review-card-list" role="list">
                {pendingReports.map((report) => {
                  const reportKey = `${report.farmerId}:${report.id}`;
                  return (
                    <button
                      key={reportKey}
                      type="button"
                      className={`review-card ${selectedReportKey === reportKey ? 'is-selected' : ''}`}
                      onClick={() => openReportModal(reportKey)}
                    >
                      <span className="review-card-type">{report.farmerName || '農家近況報告'}</span>
                      <strong>{report.title || report.highlights || '(タイトル未設定)'}</strong>
                      <span className="review-card-date">{formatDateLabel(report.publishedDate || report.month)}</span>
                      {!canReview(report) ? (
                        <span className="review-card-note">投稿本人なので承認できません</span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </article>
      </section>
      {activeModal?.type === 'post' && selectedPost ? (
        <div className="review-modal-backdrop" role="presentation" onClick={() => setActiveModal(null)}>
          <div className="review-modal" role="dialog" aria-modal="true" aria-labelledby="review-post-title" onClick={(event) => event.stopPropagation()}>
            <div className="review-detail-header">
              <div>
                <p className="review-card-type">選択中の運営記事</p>
                <h3 id="review-post-title">{selectedPost.title || '(無題)'}</h3>
              </div>
              <button className="btn btn-secondary" type="button" onClick={() => setActiveModal(null)}>閉じる</button>
            </div>
            <p className="review-card-date">{formatDateLabel(selectedPost.publishedDate)}</p>
            {selectedPost.body ? (
              <div className="review-detail-block">
                <strong>リード文</strong>
                <p>{selectedPost.body}</p>
              </div>
            ) : null}
            {Array.isArray(selectedPost.pendingChangeSummary) && selectedPost.pendingChangeSummary.length > 0 ? (
              <div className="review-detail-block">
                <strong>修正内容</strong>
                <ul className="plain-list">
                  {selectedPost.pendingChangeSummary.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            ) : null}
            {!canReview(selectedPost) ? (
              <p className="hint">自分で作成した記事は承認できません。別の管理者アカウントで承認してください。</p>
            ) : null}
            <div className="review-detail-block">
              <strong>本文</strong>
              {selectedPost.contentHtml ? (
                <div className="article article-rich-content review-detail-html" dangerouslySetInnerHTML={{ __html: selectedPost.contentHtml }} />
              ) : (
                <p>{selectedPost.body || '本文はありません。'}</p>
              )}
            </div>
            <div className="row-actions">
              {canReview(selectedPost) ? (
                <>
                  <button className="btn btn-secondary" type="button" onClick={() => handleApprovePost(selectedPost.id)}>承認</button>
                  <button className="btn btn-outline-solid" type="button" onClick={() => handleRejectPost(selectedPost.id)}>差し戻し</button>
                </>
              ) : (
                <p className="error-text">投稿本人なので承認できません。</p>
              )}
              <Link className="btn btn-outline-solid" to={`/admin/reports?editor=operator&mode=edit&postId=${selectedPost.id}`}>修正</Link>
              <button className="btn btn-danger" type="button" onClick={() => handleDeletePost(selectedPost.id)}>削除</button>
            </div>
          </div>
        </div>
      ) : null}
      {activeModal?.type === 'report' && selectedReport ? (
        <div className="review-modal-backdrop" role="presentation" onClick={() => setActiveModal(null)}>
          <div className="review-modal" role="dialog" aria-modal="true" aria-labelledby="review-report-title" onClick={(event) => event.stopPropagation()}>
            <div className="review-detail-header">
              <div>
                <p className="review-card-type">{selectedReport.farmerName || '対象農家未設定'}</p>
                <h3 id="review-report-title">{selectedReport.title || selectedReport.highlights || '(タイトル未設定)'}</h3>
              </div>
              <button className="btn btn-secondary" type="button" onClick={() => setActiveModal(null)}>閉じる</button>
            </div>
            <p className="review-card-date">{formatDateLabel(selectedReport.publishedDate || selectedReport.month)}</p>
            <div className="review-detail-block">
              <strong>対象農家</strong>
              <p>{selectedReport.farmerName || selectedReport.farmerId}</p>
            </div>
            {Array.isArray(selectedReport.pendingChangeSummary) && selectedReport.pendingChangeSummary.length > 0 ? (
              <div className="review-detail-block">
                <strong>修正内容</strong>
                <ul className="plain-list">
                  {selectedReport.pendingChangeSummary.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            ) : null}
            {!canReview(selectedReport) ? (
              <p className="hint">自分で作成した近況報告は承認できません。別の管理者アカウントで承認してください。</p>
            ) : null}
            <div className="review-detail-block">
              <strong>本文</strong>
              {selectedReport.contentHtml ? (
                <div className="article article-rich-content review-detail-html" dangerouslySetInnerHTML={{ __html: selectedReport.contentHtml }} />
              ) : (
                <p>{selectedReport.body || '本文はありません。'}</p>
              )}
            </div>
            <div className="row-actions">
              {canReview(selectedReport) ? (
                <>
                  <button className="btn btn-secondary" type="button" onClick={() => handleApproveReport(selectedReport.farmerId, selectedReport.id)}>承認</button>
                  <button className="btn btn-outline-solid" type="button" onClick={() => handleRejectReport(selectedReport.farmerId, selectedReport.id)}>差し戻し</button>
                </>
              ) : (
                <p className="error-text">投稿本人なので承認できません。</p>
              )}
              <Link className="btn btn-outline-solid" to={`/admin/reports?editor=farmer&mode=edit&farmerId=${selectedReport.farmerId}&reportId=${selectedReport.id}`}>修正</Link>
              <button className="btn btn-danger" type="button" onClick={() => handleDeleteReport(selectedReport.farmerId, selectedReport.id)}>削除</button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
